import { initializePaddle, type Paddle } from '@paddle/paddle-js'
import { paymentsApi, type PaymentsCatalog } from './api'

// Mirrors apps/web/src/lib/paddle.ts, trimmed to the single premium pass the
// funnel sells. Paddle.js delivers checkout events through one global callback;
// we register it once at init and route the active open()'s outcome to its promise.
let paddlePromise: Promise<Paddle | undefined> | null = null
let cachedCatalog: PaymentsCatalog | null = null
let pending: { resolve: () => void; reject: (e: Error) => void } | null = null

/** Fetch the payments catalog (price ids + client token) once. */
export async function getCatalog(): Promise<PaymentsCatalog> {
  if (!cachedCatalog) {
    const catalog = await paymentsApi.catalog()
    if (!catalog.checkoutEnabled) throw new Error('Checkout is not available yet')
    if (!catalog.clientToken) throw new Error('Paddle catalog is not configured')
    cachedCatalog = catalog
  }
  return cachedCatalog
}

/**
 * Localized price preview so the price we show matches the Paddle checkout exactly
 * (Paddle's recommended approach and a domain-approval requirement). Returns the
 * **total** per priceId — tax included, discount applied — in the buyer's currency.
 *
 * `total`, not `subtotal`: see the longer note on the same function in
 * apps/web/src/lib/paddle.ts. Reading `subtotal` advertised a tax-exclusive price
 * the checkout then exceeded by the local VAT rate.
 *
 * Pass a discountId to preview the discounted price. Never throws — on failure it
 * returns an empty map so callers can fall back gracefully.
 */
export async function previewPrices(priceIds: string[], discountId?: string): Promise<Record<string, string>> {
  try {
    const paddle = await getPaddle()
    const res = await paddle.PricePreview({
      items: priceIds.map((priceId) => ({ priceId, quantity: 1 })),
      ...(discountId ? { discountId } : {}),
    })
    const map: Record<string, string> = {}
    for (const li of res.data.details.lineItems) map[li.price.id] = li.formattedTotals.total
    return map
  } catch {
    return {}
  }
}

/** Lazily initialize Paddle.js using the client token + environment from the API. */
async function getPaddle(): Promise<Paddle> {
  if (!paddlePromise) {
    paddlePromise = (async () => {
      const catalog = await getCatalog()
      return initializePaddle({
        environment: catalog.environment,
        token: catalog.clientToken,
        eventCallback: (event) => {
          if (event.name === 'checkout.completed') {
            pending?.resolve()
            pending = null
          } else if (event.name === 'checkout.closed') {
            pending?.reject(new Error('Checkout closed'))
            pending = null
          } else if (event.name === 'checkout.error') {
            pending?.reject(new Error(event.detail || 'Paddle checkout failed'))
            pending = null
          }
        },
      })
    })()
  }
  const paddle = await paddlePromise
  if (!paddle) throw new Error('Paddle failed to initialize')
  return paddle
}

/**
 * Open the Paddle overlay checkout for a server-created transaction. Resolves when
 * the buyer completes payment, rejects with "Checkout closed" if they close the
 * overlay first. A supplied `customerEmail` is prefilled so the buyer doesn't retype it.
 */
export async function openCheckout(transactionId: string, customerEmail?: string): Promise<void> {
  const paddle = await getPaddle()
  return new Promise<void>((resolve, reject) => {
    pending?.reject(new Error('Checkout replaced'))
    pending = { resolve, reject }
    paddle.Checkout.open({
      transactionId,
      ...(customerEmail ? { customer: { email: customerEmail } } : {}),
      settings: {
        displayMode: 'overlay',
        variant: 'one-page',
        theme: 'light',
        ...(customerEmail ? { allowLogout: false } : {}),
      },
    })
  })
}

/**
 * Open Paddle's **inline** checkout inside a page element (`frameTarget` = the element's
 * class name) so the surrounding page stays fully Bonded-designed. Same completed/closed
 * promise contract as openCheckout. The card fields render in Paddle's iframe (whose look
 * is governed by Dashboard branding + theme); everything around it is ours.
 */
export async function openInlineCheckout(
  transactionId: string,
  frameTarget: string,
  customerEmail?: string,
): Promise<void> {
  const paddle = await getPaddle()
  return new Promise<void>((resolve, reject) => {
    pending?.reject(new Error('Checkout replaced'))
    pending = { resolve, reject }
    paddle.Checkout.open({
      transactionId,
      ...(customerEmail ? { customer: { email: customerEmail } } : {}),
      settings: {
        displayMode: 'inline',
        frameTarget,
        frameInitialHeight: 450,
        frameStyle: 'width:100%;min-width:312px;background:transparent;border:none;',
        variant: 'one-page',
        theme: 'light',
        ...(customerEmail ? { allowLogout: false } : {}),
      },
    })
  })
}
